import type { IProps } from "./markdown.types";
import ReactMarkdown from "react-markdown";
import { forwardRef, useCallback, useMemo, useState } from "react";
import remarkGfm from "remark-gfm";
import { preprocessString, preserveNewlines } from "./markdown.utilities";
import { Box } from "../box";
import { Image } from "../image";
import { MARKDOWN_COMPONENTS } from "./markdown.components";
import { CLASSNAME_REGEX, STRONG_CLASSNAME_REGEX } from "./markdown.constants";
import { MarkdownContext, NO_FAILED_IMAGE_SOURCES } from "./markdown.context";

/**
 * Markdown component for rendering markdown strings with custom styling and tooltip support.
 * @param {object} props.textProps - Props to pass to Text components.
 * @param {object} props.listProps - Props to pass to List components.
 * @param {object} props.listItemProps - Props to pass to ListItem components.
 * @param {string} props.linkTarget - Target attribute for links (default: "_blank").
 * @param {object} props.anchorProps - Props to pass to Anchor components ommiting target and rel as they are handled by the component.
 * @param {string} props.markdownString - The markdown string to render.
 * @returns {JSX.Element} The Markdown component.
 */
const Markdown = forwardRef<HTMLDivElement, IProps>(function Markdown(
    {
        textProps,
        listProps,
        imageProps,
        listItemProps,
        linkTarget = "_blank",
        markdownString,
        anchorProps,
        ...otherProps
    },
    ref
) {
    const content = useMemo(() => {
        return preserveNewlines(preprocessString(markdownString));
    }, [markdownString]);

    const ConditionalImagePreview = useMemo(() => {
        if (imageProps && imageProps.preview) {
            return Image.Preview;
        }

        return Box;
    }, [imageProps]);

    // Image sources that have failed to load. Deliberately held here, outside the markdown components, so that the
    // failure survives anything that remounts the rendered markdown: a source that has failed once is never rendered
    // as an `<img>` again, so the browser cannot request it again.
    const [failedImageSources, setFailedImageSources] = useState<ReadonlySet<string>>(NO_FAILED_IMAGE_SOURCES);

    const reportImageFailure = useCallback((source: string) => {
        // Returning the same set for an already-known failure keeps repeat error events from re-rendering.
        setFailedImageSources((sources) => (sources.has(source) ? sources : new Set(sources).add(source)));
    }, []);

    const markdownContext = useMemo(
        () => ({
            anchorProps,
            failedImageSources,
            imageProps,
            linkTarget,
            listItemProps,
            listProps,
            reportImageFailure,
            textProps,
        }),
        [
            anchorProps,
            failedImageSources,
            imageProps,
            linkTarget,
            listItemProps,
            listProps,
            reportImageFailure,
            textProps,
        ]
    );

    return (
        <Box {...otherProps}>
            <ConditionalImagePreview>
                <MarkdownContext.Provider value={markdownContext}>
                    <ReactMarkdown components={MARKDOWN_COMPONENTS} remarkPlugins={[remarkGfm]}>
                        {content}
                    </ReactMarkdown>
                </MarkdownContext.Provider>
            </ConditionalImagePreview>
        </Box>
    );
});

export { Markdown, CLASSNAME_REGEX, STRONG_CLASSNAME_REGEX };
